import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Lock, AlertCircle, Loader2 } from "lucide-react";
import logo from "@/assets/tony-knows-email-logo.webp";
import PasswordField from "./PasswordField";
import CheckInbox from "./CheckInbox";
import { friendlyAuthError, isValidEmail, isPasswordValid, isLeakedPasswordNotice } from "@/lib/authErrors";

/** Resolve where to send the user after a successful sign-in. */
const resolvePostSignInDestination = (currentPath: string): string => {
  const stored = sessionStorage.getItem("postSignInRedirect");
  if (stored) {
    sessionStorage.removeItem("postSignInRedirect");
    return stored;
  }
  // Sensible defaults based on where they were when they signed in.
  if (currentPath.startsWith("/results") || currentPath.startsWith("/career-matches") ||
      currentPath.startsWith("/blueprint") || currentPath.startsWith("/accelerator") ||
      currentPath.startsWith("/my-path")) {
    return currentPath;
  }
  if (currentPath.startsWith("/account")) return "/account";
  return "/account";
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMode?: "signin" | "signup" | "forgot";
}

type Mode = "signin" | "signup" | "forgot";
type Sent = null | { kind: "reset" | "signup"; email: string };

const SignInModal = ({ open, onOpenChange, initialMode = "signin" }: Props) => {
  const { signInWithPassword, signUpWithPassword, sendPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>(initialMode);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<Sent>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setMode("signin");
    setSent(null);
    setFormError(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) setTimeout(reset, 200);
    onOpenChange(next);
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setPassword("");
    setConfirmPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setFormError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setFormError("That email address doesn't look right.");
      return;
    }

    if (mode === "forgot") {
      setSubmitting(true);
      const { error } = await sendPasswordReset(trimmedEmail);
      setSubmitting(false);
      if (error) {
        setFormError(friendlyAuthError(error.message, "forgot"));
        return;
      }
      setSent({ kind: "reset", email: trimmedEmail });
      return;
    }

    if (!password) {
      setFormError("Please enter your password.");
      return;
    }

    if (mode === "signup") {
      if (!firstName.trim() || !lastName.trim()) {
        setFormError("Please enter your first name and surname.");
        return;
      }
      if (!isPasswordValid(password)) {
        setFormError("Password must be at least 8 characters and include a letter and a number.");
        return;
      }
      if (password !== confirmPassword) {
        setFormError("Passwords don't match.");
        return;
      }
    }

    setSubmitting(true);
    const { error } =
      mode === "signin"
        ? await signInWithPassword(trimmedEmail, password)
        : await signUpWithPassword(trimmedEmail, password, {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          });
    setSubmitting(false);

    if (error) {
      setFormError(friendlyAuthError(error.message, mode));
      return;
    }

    if (mode === "signup") {
      setSent({ kind: "signup", email: trimmedEmail });
      setPassword("");
      setConfirmPassword("");
      return;
    }

    toast({ title: "Welcome back" });
    handleClose(false);
    const dest = resolvePostSignInDestination(location.pathname);
    if (dest && dest !== location.pathname) {
      navigate(dest);
    }
  };

  const titles: Record<Mode, { title: string; desc: string; cta: string }> = {
    signin: {
      title: "Sign in to",
      desc: "Enter your email and password to continue.",
      cta: "Sign in",
    },
    signup: {
      title: "Create your account",
      desc: "Save your results and unlock your full path.",
      cta: "Create account",
    },
    forgot: {
      title: "Reset your password",
      desc: "Enter your email and we'll send you a reset link.",
      cta: "Send reset link",
    },
  };

  const t = titles[mode];

  if (sent) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="sr-only">
            <DialogTitle>Check your inbox</DialogTitle>
            <DialogDescription>Verification email sent</DialogDescription>
          </DialogHeader>
          <CheckInbox
            email={sent.email}
            kind={sent.kind}
            compact
            onChangeEmail={() => {
              setSent(null);
              setMode(sent.kind === "reset" ? "forgot" : "signup");
            }}
            onBackToSignIn={() => {
              setSent(null);
              switchMode("signin");
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  const isSignup = mode === "signup";
  const accentBubble = isSignup
    ? "bg-tk-accent/15 text-tk-accent-strong"
    : "bg-sage/15 text-sage";
  const ctaClass = isSignup
    ? "w-full rounded-full bg-tk-accent text-white hover:bg-tk-accent/90"
    : "w-full rounded-full bg-sage text-sage-foreground hover:bg-sage/90";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className={`inline-flex items-center justify-center h-12 w-12 rounded-full mb-2 mx-auto ${accentBubble}`}>
            <Lock className="h-5 w-5" />
          </div>
          <DialogTitle className="text-center text-2xl font-display">
            {mode === "signin" ? (
              <span className="inline-flex items-center justify-center gap-2 flex-wrap">
                <span>{t.title}</span>
                <img src={logo} alt="TonyKnows" className="h-6 w-auto inline-block align-middle" />
              </span>
            ) : (
              t.title
            )}
          </DialogTitle>
          <DialogDescription className="text-center">{t.desc}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" noValidate>
          {mode === "signup" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first-name">First name</Label>
                <Input
                  id="first-name"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Surname</Label>
                <Input
                  id="last-name"
                  type="text"
                  placeholder="Smith"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus={mode !== "signup"}
            />
          </div>

          {mode !== "forgot" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "signin" && (
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <PasswordField
                id="password"
                value={password}
                onChange={(v) => { setPassword(v); setFormError(null); }}
                showRules={isSignup}
                autoComplete={isSignup ? "new-password" : "current-password"}
              />
            </div>
          )}

          {mode === "signup" && (
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <PasswordField
                id="confirm-password"
                value={confirmPassword}
                onChange={(v) => { setConfirmPassword(v); setFormError(null); }}
                autoComplete="new-password"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-destructive">Passwords don't match</p>
              )}
            </div>
          )}

          {formError && (
            <div
              role={isLeakedPasswordNotice(formError) ? "status" : "alert"}
              className={
                isLeakedPasswordNotice(formError)
                  ? "flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200"
                  : "flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
              }
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <Button type="submit" className={ctaClass} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {submitting ? "Please wait..." : t.cta}
          </Button>
        </form>

        <div className="text-center text-xs text-muted-foreground mt-2">
          {mode === "signin" && (
            <>
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className="text-foreground underline hover:no-underline"
              >
                Create one
              </button>
            </>
          )}
          {mode === "signup" && (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="text-foreground underline hover:no-underline"
              >
                Sign in
              </button>
            </>
          )}
          {mode === "forgot" && (
            <button
              type="button"
              onClick={() => switchMode("signin")}
              className="text-foreground underline hover:no-underline"
            >
              Back to sign in
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignInModal;
